import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, LoginResponse } from '../types';
import { authAPI, usersAPI } from '../api';
import axios from 'axios';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => boolean;
  fetchCurrentUser: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      // Check if user is authenticated
      checkAuth: () => {
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refresh_token');
        const state = get();

        // Determine effective token considering both localStorage and state
        const effectiveToken = token || state.token;

        console.log('[AuthStore] checkAuth - token in localStorage:', !!token);
        console.log('[AuthStore] checkAuth - refresh token in localStorage:', !!refreshToken);
        console.log('[AuthStore] checkAuth - token in state:', !!state.token);
        console.log('[AuthStore] checkAuth - effective token:', !!effectiveToken);
        console.log('[AuthStore] checkAuth - isAuthenticated in state:', state.isAuthenticated);
        console.log('[AuthStore] checkAuth - user in state:', !!state.user);

        // If we have a token in localStorage but not in state, update the state
        if (token && !state.token) {
          console.log('[AuthStore] checkAuth - updating state with token from localStorage');
          set({ 
            token, 
            refreshToken: refreshToken || null,
            isAuthenticated: true 
          });
        }

        // If we have an effective token but no user data yet, fetch it
        if (effectiveToken && !state.user) {
          console.log('[AuthStore] checkAuth - fetching user data (token exists but no user data)');
          get().fetchCurrentUser().catch((error: unknown) => {
            console.error('[AuthStore] checkAuth - failed to fetch user data:', error);
          });
        }

        // Return based on effective token to avoid race conditions
        return !!effectiveToken;
      },
      
      login: async (email: string, password: string) => {
        try {
          console.log('[AuthStore] login - attempting login with email:', email);
          set({ isLoading: true, error: null });
          const response = await authAPI.login(email, password);
          console.log('[AuthStore] login - received response:', response);
          const { access_token, refresh_token, user } = response as LoginResponse & { refresh_token?: string };
          
          // Store tokens in localStorage for persistence across page refreshes
          localStorage.setItem('token', access_token);
          if (refresh_token) {
            localStorage.setItem('refresh_token', refresh_token);
          }
          
          set({
            token: access_token,
            refreshToken: refresh_token || null,
            user,
            isAuthenticated: true,
            isLoading: false,
          });
          
          console.log('[AuthStore] login - state updated, isAuthenticated:', true);
        } catch (error) {
          console.error('[AuthStore] login - error:', error);
          let errorMessage = 'Failed to login';
          
          if (axios.isAxiosError(error)) {
            if (error.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              if (error.response.status === 401) {
                errorMessage = 'Invalid credentials. Please check your email and password.';
              } else if (error.response.status === 403) {
                errorMessage = 'Your account does not have permission to access the dashboard.';
              } else {
                errorMessage = error.response.data?.message || 
                              `Server error: ${error.response.status}`;
              }
            } else if (error.request) {
              // The request was made but no response was received
              errorMessage = 'No response from server. Please check your connection.';
            } else {
              // Something happened in setting up the request that triggered an Error
              errorMessage = error.message;
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }
          
          console.error('[AuthStore] login - setting error message:', errorMessage);
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          throw error; // Re-throw to allow handling in the component
        }
      },
      
      logout: async () => {
        try {
          console.log('[AuthStore] logout - starting logout process');
          set({ isLoading: true });
          
          // Only attempt to call the logout API if we have a token
          if (get().token) {
            console.log('[AuthStore] logout - attempting to call logout API');
            try {
              await authAPI.logout();
              console.log('[AuthStore] logout - API call successful');
            } catch (error) {
              console.warn('[AuthStore] logout - Error during logout API call:', error);
              // Continue with local logout even if the API call fails
            }
          }
          
          // Always clear local state
          console.log('[AuthStore] logout - removing tokens from localStorage');
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          console.log('[AuthStore] logout - clearing state');
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          console.log('[AuthStore] logout - state cleared');
        } catch (error) {
          console.error('[AuthStore] logout - error:', error);
          let errorMessage = 'Failed to logout';
          
          if (axios.isAxiosError(error)) {
            if (error.response) {
              errorMessage = error.response.data?.message || 
                            `Server error: ${error.response.status}`;
            } else if (error.request) {
              errorMessage = 'No response from server. Please check your connection.';
            } else {
              errorMessage = error.message;
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }
          
          console.error('[AuthStore] logout - setting error message:', errorMessage);
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          // Even if the server logout fails, we'll clear the local state
          console.log('[AuthStore] logout - removing tokens from localStorage (after error)');
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          console.log('[AuthStore] logout - clearing state (after error)');
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
          });
          console.log('[AuthStore] logout - state cleared (after error)');
        }
      },
      
      clearError: () => set({ error: null }),
      
      fetchCurrentUser: async () => {
        try {
          console.log('[AuthStore] fetchCurrentUser - fetching user data');
          set({ isLoading: true, error: null });
          
          const userData = await usersAPI.getSelfUser();
          console.log('[AuthStore] fetchCurrentUser - received user data:', userData);
          
          set({
            user: userData,
            isLoading: false,
          });
        } catch (error) {
          console.error('[AuthStore] fetchCurrentUser - error:', error);
          let errorMessage = 'Failed to fetch user data';
          
          if (axios.isAxiosError(error)) {
            if (error.response?.status === 401) {
              console.log('[AuthStore] fetchCurrentUser - token invalid, clearing auth state');
              localStorage.removeItem('token');
              set({
                token: null,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: 'Session expired. Please log in again.',
              });
              return;
            } else if (error.response) {
              errorMessage = error.response.data?.message || 
                            `Server error: ${error.response.status}`;
            } else if (error.request) {
              errorMessage = 'No response from server. Please check your connection.';
            } else {
              errorMessage = error.message;
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }
          
          set({
            isLoading: false,
            error: errorMessage,
          });
        }
      },
      
      refreshAccessToken: async (): Promise<boolean> => {
        try {
          const refreshToken = localStorage.getItem('refresh_token');
          
          if (!refreshToken) {
            console.log('[AuthStore] refreshAccessToken - no refresh token available');
            return false;
          }
          
          console.log('[AuthStore] refreshAccessToken - attempting to refresh token');
          const response = await authAPI.refreshToken(refreshToken);
          
          const { access_token, refresh_token: newRefreshToken } = response;
          
          // Update tokens in localStorage
          localStorage.setItem('token', access_token);
          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
          }
          
          // Update state
          set({
            token: access_token,
            refreshToken: newRefreshToken || refreshToken,
            isAuthenticated: true,
          });
          
          console.log('[AuthStore] refreshAccessToken - token refreshed successfully');
          return true;
        } catch (error) {
          console.error('[AuthStore] refreshAccessToken - failed to refresh token:', error);
          
          // Clear tokens on refresh failure
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            error: 'Session expired. Please log in again.',
          });
          
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      // Initialize auth state from localStorage on page load
      onRehydrateStorage: () => (state) => {
        console.log('[AuthStore] onRehydrateStorage - rehydrating state');
        if (state) {
          const token = localStorage.getItem('token');
          const refreshToken = localStorage.getItem('refresh_token');
          console.log('[AuthStore] onRehydrateStorage - token in localStorage:', !!token);
          console.log('[AuthStore] onRehydrateStorage - refresh token in localStorage:', !!refreshToken);
          console.log('[AuthStore] onRehydrateStorage - token in state:', !!state.token);
          
          if (token) {
            console.log('[AuthStore] onRehydrateStorage - updating state with token from localStorage');
            state.token = token;
            state.refreshToken = refreshToken;
            state.isAuthenticated = true;
            
            if (!state.user) {
              console.log('[AuthStore] onRehydrateStorage - will fetch user data');
              setTimeout(() => {
                const store = useAuthStore.getState();
                store.fetchCurrentUser().catch((error: unknown) => {
                  console.error('[AuthStore] onRehydrateStorage - failed to fetch user data:', error);
                });
              }, 0);
            }
          } else {
            // No token in localStorage, clear state
            console.log('[AuthStore] onRehydrateStorage - no token, clearing state');
            state.token = null;
            state.refreshToken = null;
            state.user = null;
            state.isAuthenticated = false;
          }
          
          console.log('[AuthStore] onRehydrateStorage - final state:', {
            hasToken: !!state.token,
            isAuthenticated: state.isAuthenticated
          });
        } else {
          console.log('[AuthStore] onRehydrateStorage - state is null');
        }
      },
    }
  )
);                        